import { Middleware, isAction } from "@reduxjs/toolkit";
import {
  setStoreContext,
  resetPreviousStoreContext,
} from "../slices/tenant.slice";

/**
 * Multi-tenant data isolation middleware
 *
 * This middleware ensures that when a user switches between stores, any store-specific
 * data is cleared from the Redux state. This prevents data from one store leaking into another.
 *
 * When a store switch is detected (via setStoreContext action):
 * 1. Identifies which store-specific slices need to be cleared
 * 2. Resets those slices to their initial state
 * 3. Dispatches resetPreviousStoreContext to clear the tracking flags
 *
 * List of store-specific slices that should be cleared on store switch:
 * - products, categories, collections
 * - orders, customers
 * - metafields, domains, taxes, charges, pickupLocations
 * - marketingCampaigns, marketingAutomations, abandonedCart, marketingTemplates
 * - tickets, permissions, settings, dashboard
 * - And other tenant-specific data
 *
 * Non-cleared slices (user-shared across stores):
 * - auth (user info persists)
 * - ui (UI state is global)
 * - notifications (user-level)
 * - sse (websocket - global)
 */

// Slices that contain store-specific data and should be cleared on store switch
const STORE_SPECIFIC_SLICES = [
  "products",
  "category",
  "collections",
  "order",
  "customers",
  "metafields",
  "domains",
  "tax",
  "charges",
  "pickupLocations",
  "marketingCampaigns",
  "marketingAutomations",
  "abandonedCart",
  "marketingTemplates",
  "tickets",
  "permissions",
  "settings",
  "dashboard",
  "wallet",
  "page",
  "coupon",
  "discount",
  "wholesaler",
  "wholesalerCollection",
  "wholesalerProducts",
  "Inventory",
  "kyc",
  "assets",
  "webbuilder",
  "subscription",
];

export const multiTenantMiddleware: Middleware = (store) => (next) => (action) => {
    // Only process setStoreContext actions
    if (!isAction(action) || action.type !== setStoreContext.type) {
      return next(action);
    }

    const state = store.getState();

    // Detect if the store context actually changed
    const storeChanged =
      state.tenant.previousStoreId !== state.tenant.currentStoreId ||
      state.tenant.previousStoreType !== state.tenant.currentStoreType;

    // First, pass the action through to update tenant state
    const result = next(action);

    // If store changed, clear store-specific data
    if (storeChanged && state.tenant.previousStoreId) {
      console.log(
        `[MultiTenant Middleware] Store switched from ${state.tenant.previousStoreId} (${state.tenant.previousStoreType}) to ${state.tenant.currentStoreId} (${state.tenant.currentStoreType}). Clearing store-specific data...`,
      );

      // Create action objects to reset each store-specific slice
      // Each slice's initial state will be restored
      STORE_SPECIFIC_SLICES.forEach((sliceName) => {
        store.dispatch({
          type: `${sliceName}/resetState`,
          payload: undefined,
        });
      });

      // Reset the previous store tracking
      store.dispatch(resetPreviousStoreContext());
    }

    return result;
  };

export default multiTenantMiddleware;
